import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { log } from 'log';
import { omit, pick } from 'ramda';

import { dispatcher, getDeviceId } from '@core/js/backbone/exports';
import * as helpers from '@core/js/hooks/auth/helpers';
import { useRemoveQueryParams } from '@core/js/hooks/navigate';
import { htcmdService } from '@core/js/services/htcmd.service';
import { languageService } from '@core/js/services/language.service';
import { storageService } from '@core/js/services/storage.service';
import { useAppSelector, useDeviceSelector, useUserSelector } from '@core/js/store/hooks';

import { useBuyaPowaTrack } from '@interlayer/js/app/components/main/BuyaPowaReferFriend/hooks/useBuyaPowaTrack';
import { getContactPreferencesForSending } from '@interlayer/js/app/components/main/MarketingPreferences/helpers';
import { TMarketingTypesWithProduct } from '@interlayer/js/app/components/main/MarketingPreferences/types';
import { RECAPTCHA_ERROR_CODE } from '@interlayer/js/app/components/main/ReCaptchaWithTitle/constants';
import {
    TRegUnwrapData
} from '@interlayer/js/app/components/main/Registration/components/templates/OnlineReg/components/RegUnWrap/types';
import { useClientTypeAndPlatform } from '@interlayer/js/app/components/main/Registration/hooks/useClientTypeAndPlatform';
import { useDistributorByTemplate } from '@interlayer/js/app/components/main/Registration/hooks/useDistributorByTemplate';
import { useGetUtmField } from '@interlayer/js/app/components/main/Registration/hooks/useGetUtmField';
import { useGtmEventReg } from '@interlayer/js/app/components/main/Registration/hooks/useGtmEventReg';
import {
    useHandlerErrorRegistration
} from '@interlayer/js/app/components/main/Registration/hooks/useHandlerErrorRegistration';
import {
    useRegNavigationAfterLogin
} from '@interlayer/js/app/components/main/Registration/hooks/useRegNavigationAfterLogin';
import { useRegPlayerTag } from '@interlayer/js/app/components/main/Registration/hooks/useRegPlayerTag';
import { createPlayerInfo, sendBatchRequest } from '@interlayer/js/app/components/main/Registration/server';
import { TChangeDetailsProps, TRegDataMap } from '@interlayer/js/app/components/main/Registration/types';
import { useGtmEvent } from '@interlayer/js/app/hooks/analiticsSubscribe';
import { USER_PHASES } from '@interlayer/js/app/hooks/auth/constants';
import { isProceedLoginInNative } from '@interlayer/js/app/hooks/auth/helpers';
import {
    useLoginByCredentialsWithHandlingError
} from '@interlayer/js/app/hooks/auth/hooks/useLoginByCredentialsWithHandlingError';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { QUERY_PARAMS } from '@interlayer/js/app/hooks/useQueryParam/constants';
import { STORED_QUERY_PARAMS } from '@interlayer/js/app/hooks/useStoreQueryParam/constants';
import { VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import {
    setErrorRegistration,
    setIsLoadingSendForm,
    setIsRegistered,
    setIsRegistrationInProcess,
    setIsSuccessRetailReg
} from '@interlayer/js/app/store/reducers/Registration/actions';
import { ONLINE_REG_BUSINESS_PHASES } from '@interlayer/js/app/store/reducers/Registration/constants';
import { getAffiliateData } from '@interlayer/js/app/utils/affiliate.util';
import { reverseDate } from '@interlayer/js/app/utils/common.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { addZeroToStart } from '@interlayer/js/app/utils/methods.util';
import { getRefereeCookie, unsetRefereeCookie } from '@interlayer/js/app/utils/referrer.util';
import { removeUtmCookie } from '@interlayer/js/components/system/App/buzz/hooks/useSetUTMCookie';
import { useLogout } from '@interlayer/js/hooks/auth/hooks/useLogout';
import { userSetJustRegistered } from '@interlayer/js/hooks/user/store/buzz/actions';

const STATE_KEY = 'state';

export const filterFormDataOnSend = (data: (Partial<TRegUnwrapData> & {
    address?: string;
    pinReg?: string;
}) | Record<string, never> = {}, countrycode = 'GB'): TChangeDetailsProps => {
    const parsedData = {
        title: data.gender,
        firstname: data.firstname,
        lastname: data.lastname,
        birthdate: data.dateOfBirth ? reverseDate(data.dateOfBirth) : '',
        phone: data.phone,
        cellphone: data.phone,

        address: data.address || `${data.house || ''} ${data.street || ''}`.trim(),
        city: data.city,
        state: data.state || '',
        homeVenue: data.homeVenue,
        zip: data.zip,

        email: data.email,
        password: data.password,
        verificationAnswer: data.verificationAnswer,
        verificationQuestion: data.verificationQuestion,
        occupation: data.occupation,
        ...data.pinReg && { authenticationPIN: data.pinReg }
    };

    const filteredData: Record<string, string> = {};

    Object.entries(parsedData).forEach(([key, value]) => {
        if (value || key === STATE_KEY) {
            filteredData[key] = value;
        }
    });

    const addressDetails = {
        buildingNumber: data.house || data.address?.split(' ')[0] || '',
        streetName: data.street || data.address
            ?.split(' ')
            .slice(1)
            .join(' ') || ''
    };

    return {
        addressDetails,
        dataMap: filteredData,
        contactAddress: {
            addressDetails,
            countrycode,
            zip: filteredData.zip,
            city: filteredData.city
        }
    };
};

export const useRegistrationFetch = () => {
    const logout = useLogout();
    const gtmEvent = useGtmEvent();
    const gtmEventReg = useGtmEventReg();
    const getUtmField = useGetUtmField();
    const dispatch = useDispatch();
    const removeQueryParams = useRemoveQueryParams();
    const regPlayerTag = useRegPlayerTag();
    const handlerErrorRegistration = useHandlerErrorRegistration();
    const { clientType, clientPlatform } = useClientTypeAndPlatform();
    const isRepeatRequest = useRef(false);
    const [dTagDefaultHomeVenueCode = ''] = useDynamicTags([DYNAMIC_TAG_NAMES.defaultHomeVenueCode]);
    const buyaPowaTrack = useBuyaPowaTrack();

    const isRetailOrSlotReg = useDistributorByTemplate({
        slot: true,
        retail: true,
        online: false
    });
    const isRetailReg = useDistributorByTemplate({
        slot: false,
        retail: true,
        online: false
    });
    const isOnlineReg = useDistributorByTemplate({
        slot: false,
        retail: false,
        online: true
    });

    const {
        registrationsData,
        retailRegData,
        businessPhase,
        config: {
            creferer,
            customFieldNumber,
            customFieldValue
        }
    } = useRegistrationSelector();

    const {
        sessionToken: token,
        currencyCode,
        lang,
        terms: { tcVersion = '' } = {}
    } = useUserSelector(pick(['sessionToken', 'currencyCode', 'lang', 'terms']));

    const {
        deliveryPlatform,
        os: signupOsName,
        alias: signupDeviceType,
        osVersion: signupOsVersion,
        browserName: signupDeviceBrowser
    } = useDeviceSelector(pick(['deliveryPlatform', 'os', 'alias', 'osVersion', 'browserName']));

    const handlerNavigationAfterLogin = useRegNavigationAfterLogin();
    const loginAction = useLoginByCredentialsWithHandlingError();

    const {
        casinoName,
        clientVersion,
        clientSkin,
        defaultCountry,
        defaultCurrency
    } = useAppSelector(pick(['casinoName', 'clientVersion', 'clientSkin', 'defaultCountry', 'defaultCurrency']));

    const userData = isRetailOrSlotReg ? retailRegData : registrationsData;

    return useCallback(async (dataMapProp: TRegUnwrapData | Record<string, never> = {}) => {
        dispatch(setIsLoadingSendForm(true));

        let isReCaptchaError = false;
        const blackbox = helpers.getIovationBlackbox();
        const serial = getDeviceId();
        const formData: TRegUnwrapData = {
            ...Object.values(userData).reduce((acc, item) => ({ ...acc, ...item }), {}),
            ...dataMapProp
        };

        // eslint-disable-next-line camelcase
        const hallCode = storageService.local.getItem(STORED_QUERY_PARAMS.hallCode);
        formData.homeVenue = formData.homeVenue || hallCode || dTagDefaultHomeVenueCode;

        const { dataMap, contactAddress, addressDetails } = filterFormDataOnSend(formData, defaultCountry);
        const accountBusinessPhase = isRetailOrSlotReg || isOnlineReg && businessPhase === ONLINE_REG_BUSINESS_PHASES.multichannel ? USER_PHASES.multiChannel : '';

        const registrationProps: Partial<TRegDataMap> = {
            countrycode: defaultCountry,
            language: languageService.convertToIMS(lang).toUpperCase(),
            serial,
            currencyCode: currencyCode || defaultCurrency,
            signupDeviceType,
            signupDeviceId: serial,
            signupVenue: formData.homeVenue,
            signupDeliveryPlatform: deliveryPlatform.slice(0, 50),
            signupOsName,
            signupOsVersion,
            signupDeviceBrowser,
            tcVersion: String(tcVersion),
            casinoName,
            ...isRetailReg && formData.homeVenue && { custom18: formData.homeVenue },
            ...clientType && { clientType },
            ...clientPlatform && { clientPlatform },
            ...accountBusinessPhase && { accountBusinessPhase },
            ...clientSkin && { clientSkin },
            ...clientVersion && { signupClientVersion: clientVersion },
            ...dataMap,
            ...getUtmField(formData.homeVenue),
            ...getAffiliateData(),
            ...getRefereeCookie(),
            ...getRefereeCookie('ctag')
        };

        if (creferer) {
            registrationProps.creferer = registrationProps.creferer
                ? `${registrationProps.creferer};${creferer}`
                : creferer;
        }

        if (customFieldNumber) {
            registrationProps[`custom${addZeroToStart(customFieldNumber)}` as 'custom20'] = customFieldValue;
        }

        const handlerCreatePlayerInfo = (mapData: Partial<TRegDataMap>, reCaptchaToken?: string) => createPlayerInfo({
            casinoName,
            token: reCaptchaToken || token,
            blackbox,
            contactAddress,
            addressDetails,
            dataMap: mapData
        });

        const submit = (props: Partial<TRegDataMap>, reCaptchaToken?: string) => handlerCreatePlayerInfo(props, reCaptchaToken)
            .catch((error: any) => {
                if (!isRepeatRequest.current && error.response?.ID === 33006 && error.response.data.errorCode === 105) {
                    const dataWithoutTC = omit(['tcVersion'], registrationProps);

                    submit(dataWithoutTC)
                        .catch((resendError: any) => {
                            if (isReCaptchaError || htcmdService.isEnabled && !isProceedLoginInNative()) {
                                return;
                            }

                            logout({ isQuiet: true });
                            log('error', resendError);
                        })
                        .finally(() => {
                            if (isReCaptchaError) {
                                return;
                            }

                            dispatch(setIsRegistrationInProcess(false));
                            dispatch(setIsLoadingSendForm(false));
                        });
                    isRepeatRequest.current = true;

                    return Promise.reject(error);
                }

                if (error.response.data.errorCode === RECAPTCHA_ERROR_CODE) {
                    isReCaptchaError = true;

                    dispatch(setErrorRegistration({
                        isShow: true,
                        isShowReCaptcha: true,
                        reCaptchaTitleTKey: 'loginAfterRegistration',
                        reCaptchaOperation: 'signup',
                        isShowLogo: true,
                        afterReCaptchaAction: (response?: { token: string }) => {
                            isReCaptchaError = false;
                            dispatch(setIsLoadingSendForm(true));

                            submit(registrationProps, response?.token)
                                .catch((resendError: any) => {
                                    if (isReCaptchaError || htcmdService.isEnabled && !isProceedLoginInNative()) {
                                        return;
                                    }

                                    logout({ isQuiet: true });
                                    log('error', resendError);
                                })
                                .finally(() => {
                                    if (isReCaptchaError) {
                                        return;
                                    }

                                    dispatch(setIsRegistrationInProcess(false));
                                    dispatch(setIsLoadingSendForm(false));
                                });
                        }
                    }));

                    dispatch(setIsLoadingSendForm(false));

                    return Promise.reject();
                }

                handlerErrorRegistration(error);

                return Promise.reject(error);
            })
            .then(() => {
                removeUtmCookie();
                unsetRefereeCookie();
                dispatch(setIsRegistrationInProcess(true));
                dispatch(userSetJustRegistered(true));
                dispatch(setIsRegistered(true));

                gtmEvent('registration_completed', {
                    // eslint-disable-next-line camelcase
                    first_name: formData[VALIDATION_FIELDS.firstname],
                    // eslint-disable-next-line camelcase
                    last_name: formData[VALIDATION_FIELDS.lastname],
                    // eslint-disable-next-line camelcase
                    email_address: formData[VALIDATION_FIELDS.email],
                    city: formData[VALIDATION_FIELDS.city],
                    county: formData[VALIDATION_FIELDS.state],
                    postcode: formData[VALIDATION_FIELDS.zip],
                    // eslint-disable-next-line camelcase
                    phone_number: formData[VALIDATION_FIELDS.phone]
                });

                const preferenceRawMap = Object.entries(formData).reduce((acc: TMarketingTypesWithProduct, pair) => {
                    acc[pair[0]] = Boolean(pair[1]);
                    return acc;
                }, {});

                return sendBatchRequest({
                    authParameters: {
                        casinoName,
                        email: dataMap.email || ''
                    },
                    tags: regPlayerTag,
                    contactPreferences: getContactPreferencesForSending(preferenceRawMap)
                }).catch((error: any) => log('error', error));
            })
            .then(() => {
                if (isRetailOrSlotReg) {
                    dispatch(setIsSuccessRetailReg(true));
                    return;
                }

                const wrapperLoginAction = () => loginAction(formData.email, formData.password, false, true)
                    .then((response) => {
                        // @ts-ignore: remove after loginAction is ts
                        handlerNavigationAfterLogin(response, false);

                        buyaPowaTrack(response);
                    })
                    .catch((error) => {
                        if (error?.data?.errorCode === RECAPTCHA_ERROR_CODE) {
                            dispatch(setErrorRegistration({
                                isShow: true,
                                isShowReCaptcha: true,
                                reCaptchaTitleTKey: 'loginAfterRegistration',
                                reCaptchaOperation: 'login',
                                isShowLogo: true,
                                afterReCaptchaAction: wrapperLoginAction
                            }));

                            return;
                        }

                        return Promise.reject(error);
                    });

                return wrapperLoginAction();
            });

        return submit(registrationProps)
            .catch((error: any) => {
                if (isReCaptchaError || htcmdService.isEnabled && !isProceedLoginInNative()) {
                    return;
                }

                logout({ isQuiet: true });
                log('error', error);
                dispatcher.dispatch('registration:error', error);
            })
            .finally(() => {
                if (isReCaptchaError) {
                    return;
                }

                dispatch(setIsRegistrationInProcess(false));
                dispatch(setIsLoadingSendForm(false));

                removeQueryParams(QUERY_PARAMS.buyaPowaReferFriendMetaData);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        casinoName, clientSkin, clientType, clientPlatform, clientVersion, creferer, currencyCode, customFieldNumber,
        customFieldValue, dTagDefaultHomeVenueCode, defaultCountry, deliveryPlatform, dispatch, getUtmField, gtmEventReg,
        handlerErrorRegistration, handlerNavigationAfterLogin, isRetailOrSlotReg, lang, loginAction, logout, regPlayerTag,
        signupDeviceBrowser, signupDeviceType, signupOsName, signupOsVersion, tcVersion, token, userData,
        isRetailReg
    ]);
};
